import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardList, Navigation, Phone, Store } from "lucide-react";
import { toast } from "react-toastify";
import { IEstablishment } from "../../interfaces/establishment";
import {
    EstablishmentMyContext, getEstablishment, getEstablishmentMyContext, getFounderEstablishmentCounts, viewEstablishment,
} from "../../interfaces/establishments";
import { createProspect } from "../../interfaces/prospect";
import { FounderCounts } from "../../lib/founderCounts";
import { getUpcomingAnniversary } from "../../lib/anniversary";
import { Button } from "../../components/ui/Button";
import { displayName, history, mapsLinks, people as readPeople, tradingStatus, whyNow } from "./detail/model";
import { Header } from "./detail/Header";
import { CommercialProfile, DecisionMakers, Reviews, WhyNow } from "./detail/Sections";
import { History } from "./detail/History";
import { Company, Coordinates, FollowUp, NetworkCard } from "./detail/Sidebar";

/**
 * The establishment fiche, laid out in the order a rep decides in: is it open → why call now →
 * who decides and how to reach them → what to pitch → what happened → the legal record.
 * The right rail reads Réseau (how big the account really is) → their own follow-up (CRM status,
 * next action, notes, lists) → Coordonnées.
 * One scrolling page, no tabs: the decision maker and the signal have to be seen together.
 */
export default function EstablishmentDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [establishment, setEstablishment] = useState<IEstablishment | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [context, setContext] = useState<EstablishmentMyContext | null>(null);
    const [counts, setCounts] = useState<Record<string, FounderCounts>>({});
    const [crmBusy, setCrmBusy] = useState(false);
    const [focus, setFocus] = useState<{ id: string; n: number } | null>(null);

    useEffect(() => {
        if (!id) return;
        let alive = true;
        setLoading(true);
        setFailed(false);
        setContext(null);
        setCounts({});
        getEstablishment(id)
            .then((data) => alive && setEstablishment(data))
            .catch(() => alive && setFailed(true))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [id]);

    // The URL may carry a SIRET or a SIREN; everything after the first read uses the real id.
    const estId = establishment?._id;
    useEffect(() => {
        if (!estId) return;
        let alive = true;
        getEstablishmentMyContext(estId)
            .then((c) => alive && setContext(c))
            .catch(() => alive && setContext({ prospect: null, lists: [], news: [] }));
        viewEstablishment(estId).catch(() => {});
        return () => { alive = false; };
    }, [estId]);

    useEffect(() => {
        const ids = (establishment?.peoples || []).map((p: any) => p?._id).filter(Boolean) as string[];
        if (ids.length === 0) return;
        getFounderEstablishmentCounts(ids).then(setCounts).catch(() => {});
    }, [establishment]);

    const derived = useMemo(() => {
        if (!establishment) return null;
        const status = tradingStatus(establishment);
        const team = readPeople(establishment);
        const items = history(establishment, context?.news || []);
        const anniversary = status.closed
            ? null
            : getUpcomingAnniversary(establishment.operations?.opening_date || establishment.registration?.creation_date);
        return {
            status,
            team,
            lead: team[0] || null,
            items,
            why: status.closed ? [] : whyNow(establishment, items, anniversary),
        };
    }, [establishment, context?.news]);

    const addToCrm = useCallback(async () => {
        if (!establishment || !derived) return;
        const lead = derived.lead;
        const [firstName, ...rest] = (lead?.name || "").trim().split(/\s+/);
        setCrmBusy(true);
        const created = await createProspect({
            name: lead?.name || displayName(establishment.name),
            firstName: firstName || undefined,
            lastName: rest.join(" ") || undefined,
            title: lead?.mandate,
            company: displayName(establishment.name),
            email: establishment.email || lead?.email || undefined,
            phone: establishment.phone || lead?.phone || undefined,
            website: establishment.website_url,
            linkedinUrl: lead?.linkedinUrl || undefined,
            location: [establishment.address?.city, establishment.address?.postcode && `(${establishment.address.postcode})`].filter(Boolean).join(" ") || undefined,
            establishmentId: establishment._id,
            source: "manual",
            status: "new",
        });
        setCrmBusy(false);
        if (created?._id) {
            setContext((c) => ({ prospect: created, lists: c?.lists || [], news: c?.news || [] }));
            toast.success("Ajouté à votre CRM");
        }
    }, [establishment, derived]);

    const onListAdded = useCallback((list: { _id: string; name: string }) => {
        setContext((c) => {
            const lists = c?.lists || [];
            if (lists.some((l) => l._id === list._id)) return c;
            return { prospect: c?.prospect ?? null, news: c?.news || [], lists: [list, ...lists] };
        });
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-canvas px-4 py-6 md:px-8">
                <div className="mx-auto max-w-6xl space-y-6 motion-safe:animate-pulse">
                    <div className="h-36 rounded-2xl border border-border bg-surface" />
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <div className="h-96 rounded-2xl border border-border bg-surface lg:col-span-2" />
                        <div className="h-96 rounded-2xl border border-border bg-surface" />
                    </div>
                </div>
            </div>
        );
    }

    if (failed || !establishment || !derived) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
                <div className="rounded-2xl border border-border bg-surface p-10 text-center">
                    <Store className="mx-auto mb-4 h-10 w-10 text-ink-faint" />
                    <p className="mb-5 font-sans text-sm text-ink-muted">Établissement introuvable.</p>
                    <Button size="sm" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4" />
                        Retour
                    </Button>
                </div>
            </div>
        );
    }

    const { status, team, lead, items, why } = derived;
    const phone = establishment.phone || lead?.phone || null;
    const maps = mapsLinks(establishment);

    return (
        <div className="min-h-screen bg-canvas px-4 pb-24 pt-6 font-sans md:px-8 lg:pb-6">
            <div className="mx-auto max-w-6xl space-y-6">
                <Header
                    establishment={establishment}
                    status={status}
                    lead={lead}
                    leadCounts={lead ? counts[lead.id] : undefined}
                    phone={phone}
                    context={context}
                    crmBusy={crmBusy}
                    onAddToCrm={addToCrm}
                    onListAdded={onListAdded}
                />

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="min-w-0 space-y-6 lg:col-span-2">
                        {!status.closed && (
                            <WhyNow
                                items={why}
                                lastEvent={items[0]}
                                onOpen={(historyId) => setFocus((f) => ({ id: historyId, n: (f?.n || 0) + 1 }))}
                            />
                        )}
                        <DecisionMakers people={team} counts={counts} closed={status.closed} />
                        <CommercialProfile establishment={establishment} />
                        <History items={items} focus={focus} />
                        <Reviews establishment={establishment} />
                        <Company establishment={establishment} />
                    </div>

                    <aside className="min-w-0 space-y-6 lg:sticky lg:top-6 lg:self-start">
                        <NetworkCard establishment={establishment} people={team} counts={counts} />
                        <FollowUp
                            establishmentId={establishment._id}
                            context={context}
                            crmBusy={crmBusy}
                            onAddToCrm={addToCrm}
                            onProspectChange={(p) => setContext((c) => (c ? { ...c, prospect: p } : c))}
                            onListAdded={onListAdded}
                        />
                        <Coordinates establishment={establishment} />
                    </aside>
                </div>
            </div>

            {/* On a phone, in front of the restaurant: the three things one does from here. */}
            <nav className="fixed inset-x-0 bottom-0 z-30 flex gap-2 border-t border-border bg-surface px-4 py-3 lg:hidden" aria-label="Actions rapides">
                {phone && !status.closed && (
                    <a href={`tel:${phone}`} className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-ink font-sans text-sm font-medium text-ink-inverse focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        <Phone className="h-4 w-4" />
                        Appeler
                    </a>
                )}
                <a href={maps.directions} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-border-strong font-sans text-sm font-medium text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    <Navigation className="h-4 w-4" />
                    Itinéraire
                </a>
                <a href="#suivi" className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-border-strong font-sans text-sm font-medium text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    <ClipboardList className="h-4 w-4" />
                    Suivi
                </a>
            </nav>
        </div>
    );
}
